/**
 * Phantom Node — Frontend
 *
 * One file. Plain React: useState / useEffect / useRef. No router, no
 * state library, no fancy abstractions. The whole app is a five-state
 * machine driven by `status`:
 *
 *   idle        → Welcome / briefing
 *   armed       → A qubit is in flight; 5s timer running; pick Z or X
 *   resolving   → POST /api/intercept is in-flight
 *   debrief     → Round resolved; user reads the result and clicks
 *                 "Next Packet" to draw a new round (manual advance)
 *   compromised → Alarm hit 100%; full reset
 *
 * Every quantum decision (QASM string + measurement statistics) is made
 * by the Cloudflare Worker. This file owns ONLY the classical game.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  Activity,
  AlertTriangle,
  Atom,
  Binary,
  Bomb,
  BookOpen,
  ChevronRight,
  Cpu,
  Crosshair,
  Eye,
  Flame,
  Gauge,
  KeyRound,
  Power,
  Radio,
  RadioTower,
  ShieldAlert,
  Skull,
  Square,
  Target,
  Terminal,
  Timer,
  Waves,
  Zap,
} from "lucide-react";
import type {
  Basis,
  Bit,
  CompletedRound,
  GameStatus,
  InterceptPayload,
  InterceptResponse,
  RoundSetup,
} from "./types";

// ───────────────────────────────────────────────────────────────────
// Constants — knobs you'll be asked about in the pitch
// ───────────────────────────────────────────────────────────────────

const API_URL: string =
  (import.meta.env.VITE_API_URL as string | undefined) ??
  "http://127.0.0.1:8787";

const COHERENCE_MS = 5000; // qubit decoherence window per round
const TICK_MS = 50; // timer redraw cadence
const ALARM_STEP = 25; // QBER alarm gain per detected interception

type Tone = "neon" | "alert" | "warn" | "ok" | "dim" | "info";

// ───────────────────────────────────────────────────────────────────
// Pure helpers (no React, easy to explain)
// ───────────────────────────────────────────────────────────────────

const randomBit = (): Bit => (Math.random() < 0.5 ? 0 : 1);
const randomBasis = (): Basis => (Math.random() < 0.5 ? "Z" : "X");

const makeRound = (): RoundSetup => ({
  alice_bit: randomBit(),
  alice_basis: randomBasis(),
  bob_basis: randomBasis(),
});

const basisLabel = (b: Basis): string =>
  b === "Z" ? "Rectilinear (Z)" : "Diagonal (X)";

const ketLabel = (bit: Bit, basis: Basis): string => {
  if (basis === "Z") return bit === 0 ? "|0⟩" : "|1⟩";
  return bit === 0 ? "|+⟩" : "|−⟩";
};

const TONE_TEXT: Record<Tone, string> = {
  neon: "text-terminal-neon",
  alert: "text-terminal-alert",
  warn: "text-terminal-warn",
  ok: "text-terminal-ok",
  dim: "text-terminal-dim",
  info: "text-terminal-info",
};

const TONE_BORDER: Record<Tone, string> = {
  neon: "border-terminal-neon/50",
  alert: "border-terminal-alert/50",
  warn: "border-terminal-warn/50",
  ok: "border-terminal-ok/50",
  dim: "border-terminal-border",
  info: "border-terminal-info/50",
};

// ───────────────────────────────────────────────────────────────────
// Small generic primitives
// ───────────────────────────────────────────────────────────────────

function Panel({
  title,
  icon,
  glow,
  children,
  className = "",
  right,
}: {
  title: string;
  icon: React.ReactNode;
  glow?: "neon" | "alert" | "warn" | "info" | "ok";
  children: React.ReactNode;
  className?: string;
  right?: React.ReactNode;
}) {
  const glowClass =
    glow === "alert"
      ? "shadow-alert"
      : glow === "warn"
        ? "shadow-warn"
        : glow === "info"
          ? "shadow-info"
          : glow === "ok"
            ? "shadow-ok"
            : "shadow-neon";
  return (
    <section
      className={`relative bg-terminal-panel/80 backdrop-blur-sm border border-terminal-border rounded-md ${glowClass} ${className}`}
    >
      <header className="flex items-center justify-between px-4 py-2 border-b border-terminal-border bg-terminal-grid/60">
        <div className="flex items-center gap-2 text-terminal-neon text-xs uppercase tracking-[0.25em] font-bold">
          {icon}
          <span>{title}</span>
        </div>
        <div className="flex items-center gap-3">
          {right}
          <div className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-terminal-ok/80" />
            <span className="h-2 w-2 rounded-full bg-terminal-warn/80" />
            <span className="h-2 w-2 rounded-full bg-terminal-alert/80" />
          </div>
        </div>
      </header>
      <div className="p-4">{children}</div>
    </section>
  );
}

function Stat({
  label,
  value,
  icon,
  tone = "neon",
}: {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  tone?: Tone;
}) {
  return (
    <div className="flex items-center gap-3 bg-terminal-grid/40 border border-terminal-border rounded px-3 py-2">
      <div className={TONE_TEXT[tone]}>{icon}</div>
      <div className="flex flex-col leading-tight">
        <span className="text-[10px] uppercase tracking-[0.25em] text-terminal-dim">
          {label}
        </span>
        <span className={`text-lg font-bold ${TONE_TEXT[tone]}`}>{value}</span>
      </div>
    </div>
  );
}

function CoherenceBar({
  remainingMs,
  active,
}: {
  remainingMs: number;
  active: boolean;
}) {
  const pct = Math.max(0, Math.min(100, (remainingMs / COHERENCE_MS) * 100));
  const tone =
    pct > 60
      ? "bg-terminal-ok"
      : pct > 30
        ? "bg-terminal-warn"
        : "bg-terminal-alert";
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.25em] text-terminal-dim">
        <span className="flex items-center gap-1.5">
          <Timer size={12} className="text-terminal-neon" /> Coherence Window
        </span>
        <span className={active ? "text-terminal-neon" : "text-terminal-dim"}>
          {(remainingMs / 1000).toFixed(2)}s
        </span>
      </div>
      <div className="h-2 w-full bg-terminal-grid rounded-sm overflow-hidden border border-terminal-border">
        <div
          className={`h-full ${tone} transition-[width] duration-75 ease-linear`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function AlarmMeter({ value }: { value: number }) {
  const filled = Math.round(value / ALARM_STEP);
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.25em] text-terminal-dim">
        <span className="flex items-center gap-1.5">
          <ShieldAlert size={12} className="text-terminal-alert" /> Detection
          Alarm
        </span>
        <span
          className={
            value >= 75
              ? "text-terminal-alert"
              : value >= 50
                ? "text-terminal-warn"
                : "text-terminal-dim"
          }
        >
          {value}%
        </span>
      </div>
      <div className="grid grid-cols-4 gap-1">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className={`h-2 rounded-sm border ${
              i < filled
                ? "bg-terminal-alert/80 border-terminal-alert shadow-alert"
                : "bg-terminal-grid border-terminal-border"
            }`}
          />
        ))}
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
// Channel diagram — Alice → Eve → Bob, with optional qubit overlay.
// Pure CSS/HTML, no SVG — easy to explain in the pitch.
// ───────────────────────────────────────────────────────────────────

function ChannelDiagram({
  alice_basis,
  alice_bit,
  eve_basis, // null until Eve picks
  bob_basis,
  showAliceState, // hide during play (Eve doesn't know)
  showBobResult,
  bob_result,
  collapsed,
}: {
  alice_basis: Basis;
  alice_bit?: Bit;
  eve_basis: Basis | null;
  bob_basis: Basis;
  showAliceState: boolean;
  showBobResult: boolean;
  bob_result?: Bit;
  collapsed?: boolean;
}) {
  return (
    <div className="relative grid grid-cols-3 gap-2 items-stretch">
      <Node
        title="Alice"
        tone="ok"
        icon={<RadioTower size={18} />}
        rows={[
          ["basis", basisLabel(alice_basis)],
          showAliceState && alice_bit !== undefined
            ? ["bit", String(alice_bit)]
            : ["bit", "sealed"],
          showAliceState && alice_bit !== undefined
            ? ["state", ketLabel(alice_bit, alice_basis)]
            : ["state", "?"],
        ]}
      />
      <Node
        title="Eve · you"
        tone={eve_basis ? (collapsed ? "alert" : "neon") : "warn"}
        icon={<Skull size={18} />}
        rows={[
          ["basis", eve_basis ? basisLabel(eve_basis) : "—"],
          ["splice", eve_basis ? "engaged" : "awaiting"],
          [
            "collapse",
            eve_basis ? (collapsed ? "yes" : "no") : "—",
          ],
        ]}
      />
      <Node
        title="Bob"
        tone={
          showBobResult && bob_result !== undefined && alice_bit !== undefined
            ? bob_result === alice_bit
              ? "ok"
              : "alert"
            : "info"
        }
        icon={<Radio size={18} />}
        rows={[
          ["basis", basisLabel(bob_basis)],
          [
            "measured",
            showBobResult && bob_result !== undefined ? String(bob_result) : "?",
          ],
          [
            "vs. Alice",
            showBobResult && bob_result !== undefined && alice_bit !== undefined
              ? bob_result === alice_bit
                ? "match"
                : "MISMATCH"
              : "—",
          ],
        ]}
      />

      {/* Connecting lines drawn as absolute pseudo-elements */}
      <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 pointer-events-none flex items-center px-[16.66%]">
        <Wire />
        <Wire />
      </div>
    </div>
  );
}

function Wire() {
  return (
    <div className="flex-1 h-px bg-gradient-to-r from-terminal-neon/0 via-terminal-neon/40 to-terminal-neon/0" />
  );
}

function Node({
  title,
  tone,
  icon,
  rows,
}: {
  title: string;
  tone: Tone;
  icon: React.ReactNode;
  rows: [string, string][];
}) {
  return (
    <div
      className={`relative z-10 border rounded p-3 bg-terminal-panel/95 ${TONE_BORDER[tone]} ${TONE_TEXT[tone]}`}
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] font-bold mb-2">
        {icon}
        <span>{title}</span>
      </div>
      <dl className="space-y-1">
        {rows.map(([k, v]) => (
          <div
            key={k}
            className="flex items-center justify-between text-xs gap-2"
          >
            <dt className="text-terminal-dim uppercase tracking-[0.15em]">
              {k}
            </dt>
            <dd className="text-terminal-fg font-bold text-right truncate">
              {v}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
// QASM viewer
// ───────────────────────────────────────────────────────────────────

function QasmBlock({ qasm }: { qasm: string | null }) {
  return (
    <Panel title="Live QASM Visualizer" icon={<Cpu size={14} />}>
      <div className="text-[11px] uppercase tracking-[0.25em] text-terminal-dim mb-2 flex items-center justify-between">
        <span>OPENQASM 2.0 — generated this round by the worker</span>
        <span className="flex items-center gap-1.5 text-terminal-ok">
          <span className="h-1.5 w-1.5 rounded-full bg-terminal-ok animate-pulse" />
          dynamic
        </span>
      </div>
      <pre className="terminal-scroll bg-terminal-bg border border-terminal-border rounded p-3 text-xs leading-relaxed text-terminal-fg/90 max-h-72 overflow-auto whitespace-pre">
        {qasm
          ? colorizeQasm(qasm)
          : "// awaiting first interception…\n// each round programmatically emits a fresh BB84 circuit:\n//   Alice prepares  →  Eve intercepts  →  Bob measures"}
      </pre>
    </Panel>
  );
}

function colorizeQasm(qasm: string): React.ReactNode {
  // Cheap, line-based syntactic colourisation. No real parser — easy to explain.
  return qasm.split("\n").map((line, i) => {
    const trimmed = line.trimStart();
    let cls = "text-terminal-fg/90";
    if (trimmed.startsWith("//")) cls = "text-terminal-dim italic";
    else if (trimmed.startsWith("OPENQASM") || trimmed.startsWith("include"))
      cls = "text-terminal-warn";
    else if (trimmed.startsWith("qreg") || trimmed.startsWith("creg"))
      cls = "text-terminal-info";
    else if (trimmed.startsWith("barrier")) cls = "text-terminal-dim";
    else if (trimmed.startsWith("measure")) cls = "text-terminal-alert";
    else if (/^[hxyz]\s/.test(trimmed)) cls = "text-terminal-ok";
    return (
      <div key={i} className={cls}>
        {line || " "}
      </div>
    );
  });
}

// ───────────────────────────────────────────────────────────────────
// Pitch-style welcome / hero
// ───────────────────────────────────────────────────────────────────

function Hero({ onBegin }: { onBegin: () => void }) {
  return (
    <section className="relative border border-terminal-border rounded-lg overflow-hidden bg-terminal-panel/70 backdrop-blur-sm shadow-neon">
      <div className="absolute inset-0 pointer-events-none opacity-60">
        <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-terminal-neon/15 blur-3xl" />
        <div className="absolute -bottom-32 -right-24 h-80 w-80 rounded-full bg-terminal-info/15 blur-3xl" />
      </div>
      <div className="relative p-8 md:p-12 grid gap-8 md:grid-cols-[1fr_auto] items-center">
        <div className="space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-terminal-neon/40 bg-terminal-neon/5 text-[10px] uppercase tracking-[0.4em] text-terminal-neon">
            <Atom size={12} /> BB84 · Quantum Cyber-Warfare
          </div>
          <h1 className="text-4xl md:text-6xl font-black tracking-[0.04em] uppercase leading-none">
            <span className="text-terminal-neon">Phantom</span>{" "}
            <span className="text-terminal-fg">Node</span>
          </h1>
          <p className="text-base md:text-lg text-terminal-fg/90 leading-relaxed max-w-2xl">
            A playable proof of why quantum cryptography is unbreakable. You're
            <span className="text-terminal-alert font-bold"> Eve</span>, spliced
            into Alice and Bob's quantum channel. Steal the key — without the
            observer effect ratting you out.
          </p>
          <div className="flex flex-wrap gap-2 text-[10px] uppercase tracking-[0.3em]">
            <Pill tone="neon">React · Vite · Tailwind</Pill>
            <Pill tone="info">Cloudflare Worker</Pill>
            <Pill tone="warn">OpenQASM 2.0 · dynamic</Pill>
            <Pill tone="ok">closed-form BB84 physics</Pill>
          </div>
        </div>
        <div className="flex flex-col items-stretch gap-3 md:min-w-[260px]">
          <button
            onClick={onBegin}
            className="py-5 px-6 rounded-md border border-terminal-neon/70 text-terminal-neon font-extrabold uppercase tracking-[0.35em] text-base bg-terminal-neon/10 hover:bg-terminal-neon/20 hover:shadow-neon transition"
          >
            <span className="inline-flex items-center gap-3 justify-center w-full">
              <Zap size={18} /> Begin Operation
            </span>
            <span className="block mt-2 text-[10px] tracking-[0.4em] text-terminal-dim font-mono">
              press SPACE
            </span>
          </button>
          <div className="text-[10px] uppercase tracking-[0.3em] text-terminal-dim text-center">
            One qubit per round · 5.0s window · You decide
          </div>
        </div>
      </div>
    </section>
  );
}

function Pill({
  tone,
  children,
}: {
  tone: Tone;
  children: React.ReactNode;
}) {
  return (
    <span
      className={`px-2 py-1 rounded border ${TONE_BORDER[tone]} ${TONE_TEXT[tone]} bg-terminal-grid/40 font-bold`}
    >
      {children}
    </span>
  );
}

function ArchitectureStrip() {
  // The single most-asked pitch question: "how is it actually built?"
  // This strip answers it visually so the answer is in muscle memory.
  return (
    <Panel title="Architecture" icon={<Cpu size={14} />} glow="info">
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto_1fr] gap-3 items-stretch text-xs">
        <ArchBox
          tone="neon"
          title="Frontend"
          subtitle="Classical game · React + Vite + Tailwind"
          lines={["Round RNG", "5s coherence timer", "Score / streak / alarm"]}
        />
        <ArchArrow label="POST /api/intercept" />
        <ArchBox
          tone="info"
          title="Cloudflare Worker"
          subtitle="Edge — TypeScript, ES modules"
          lines={[
            "Validates payload",
            "Builds OpenQASM 2.0 string",
            "Computes BB84 statistics",
          ]}
        />
        <ArchArrow label="JSON response" reverse />
        <ArchBox
          tone="warn"
          title="QASM + Result"
          subtitle="Dynamic per round — no hardcoded circuit"
          lines={[
            "qasm_generated",
            "eve_result · bob_result",
            "wavefunction_collapsed",
          ]}
        />
      </div>
    </Panel>
  );
}

function ArchBox({
  tone,
  title,
  subtitle,
  lines,
}: {
  tone: Tone;
  title: string;
  subtitle: string;
  lines: string[];
}) {
  return (
    <div
      className={`rounded border ${TONE_BORDER[tone]} ${TONE_TEXT[tone]} bg-terminal-grid/40 p-3`}
    >
      <div className="font-bold uppercase tracking-[0.25em] text-xs">
        {title}
      </div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-terminal-dim mt-0.5">
        {subtitle}
      </div>
      <ul className="mt-2 space-y-1">
        {lines.map((l) => (
          <li
            key={l}
            className="text-terminal-fg/90 text-xs flex items-center gap-1.5"
          >
            <ChevronRight size={10} className="text-terminal-dim shrink-0" />
            <span>{l}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ArchArrow({
  label,
  reverse,
}: {
  label: string;
  reverse?: boolean;
}) {
  return (
    <div className="hidden md:flex flex-col items-center justify-center gap-1 text-terminal-dim">
      <div className="text-[10px] uppercase tracking-[0.3em]">{label}</div>
      <div className="flex items-center gap-1 text-terminal-neon">
        {reverse ? (
          <>
            <span>←</span>
            <span className="h-px w-12 bg-terminal-neon/40" />
            <span>·</span>
          </>
        ) : (
          <>
            <span>·</span>
            <span className="h-px w-12 bg-terminal-neon/40" />
            <span>→</span>
          </>
        )}
      </div>
    </div>
  );
}

function CastSection() {
  return (
    <Panel title="The Cast" icon={<Radio size={14} />}>
      <div className="grid gap-3 md:grid-cols-3">
        <CastCard
          tone="ok"
          icon={<RadioTower size={18} />}
          name="Alice"
          line="Sender. Each round picks a random bit (0/1) and a random basis (Z/X), prepares one qubit accordingly, and ships it down the channel."
        />
        <CastCard
          tone="alert"
          icon={<Skull size={18} />}
          name="Eve · you"
          line="Phantom node. Splice the line, pick a measurement basis, re-prepare the qubit, and forward to Bob — undetected if you can."
        />
        <CastCard
          tone="info"
          icon={<Radio size={18} />}
          name="Bob"
          line="Receiver. Measures in his own random basis. He compares notes with Alice; mismatches trip the QBER detector."
        />
      </div>
    </Panel>
  );
}

function CastCard({
  tone,
  icon,
  name,
  line,
}: {
  tone: Tone;
  icon: React.ReactNode;
  name: string;
  line: string;
}) {
  return (
    <div
      className={`rounded border ${TONE_BORDER[tone]} bg-terminal-grid/40 p-4`}
    >
      <div
        className={`flex items-center gap-2 font-bold uppercase tracking-[0.25em] text-xs ${TONE_TEXT[tone]}`}
      >
        {icon}
        <span>{name}</span>
      </div>
      <p className="text-terminal-fg/90 text-xs mt-2 leading-relaxed">{line}</p>
    </div>
  );
}

function BasesSection() {
  return (
    <Panel title="The Two Bases" icon={<BookOpen size={14} />}>
      <div className="grid gap-3 md:grid-cols-2">
        <div className="rounded border border-terminal-neon/40 bg-terminal-neon/5 p-4">
          <div className="flex items-center justify-between">
            <span className="text-terminal-neon font-bold tracking-[0.25em] uppercase text-sm">
              [Z] Rectilinear
            </span>
            <span className="text-terminal-dim text-sm">|0⟩ · |1⟩</span>
          </div>
          <p className="text-terminal-fg/90 text-xs mt-2 leading-relaxed">
            Vertical / horizontal polarisation. If your basis matches Alice's,
            measurement is deterministic — you read the bit cleanly with no
            collapse.
          </p>
        </div>
        <div className="rounded border border-terminal-warn/40 bg-terminal-warn/5 p-4">
          <div className="flex items-center justify-between">
            <span className="text-terminal-warn font-bold tracking-[0.25em] uppercase text-sm">
              [X] Diagonal
            </span>
            <span className="text-terminal-dim text-sm">|+⟩ · |−⟩</span>
          </div>
          <p className="text-terminal-fg/90 text-xs mt-2 leading-relaxed">
            ±45° polarisation. Mismatch the basis and the result is a coin-flip
            — you collapse the wavefunction and Bob sees the damage.
          </p>
        </div>
      </div>
    </Panel>
  );
}

function ScoringSection() {
  return (
    <Panel title="Scoring & Controls" icon={<Gauge size={14} />}>
      <div className="grid gap-4 md:grid-cols-2">
        <ul className="space-y-2 text-xs leading-relaxed">
          <li className="flex gap-2">
            <KeyRound size={14} className="text-terminal-ok shrink-0 mt-0.5" />
            <span>
              <span className="text-terminal-ok font-bold">Clean steal</span> —
              Bob's bit matches Alice's. Keys +1, streak +1.
            </span>
          </li>
          <li className="flex gap-2">
            <ShieldAlert
              size={14}
              className="text-terminal-alert shrink-0 mt-0.5"
            />
            <span>
              <span className="text-terminal-alert font-bold">Detected</span> —
              Bob disagrees. Alarm +25%, streak resets.
            </span>
          </li>
          <li className="flex gap-2">
            <Timer size={14} className="text-terminal-warn shrink-0 mt-0.5" />
            <span>
              <span className="text-terminal-warn font-bold">Timeout</span> —
              5.0s window expires before you pick. Streak resets.
            </span>
          </li>
          <li className="flex gap-2">
            <Bomb size={14} className="text-terminal-alert shrink-0 mt-0.5" />
            <span>
              <span className="text-terminal-alert font-bold">Compromised</span>{" "}
              — alarm hits 100%, operation burned, full reset.
            </span>
          </li>
        </ul>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <ControlRow keys={["Z"]} label="Measure rectilinear" />
          <ControlRow keys={["X"]} label="Measure diagonal" />
          <ControlRow keys={["Space"]} label="Begin / next packet" />
          <ControlRow keys={["Esc"]} label="Stop / abort" />
        </div>
      </div>
    </Panel>
  );
}

function ControlRow({ keys, label }: { keys: string[]; label: string }) {
  return (
    <div className="flex items-center gap-2 bg-terminal-grid/40 border border-terminal-border rounded px-2 py-2">
      <div className="flex gap-1">
        {keys.map((k) => (
          <kbd key={k} className="kbd">
            {k}
          </kbd>
        ))}
      </div>
      <span className="text-terminal-fg/90 truncate">{label}</span>
    </div>
  );
}

function WelcomeScreen({ onBegin }: { onBegin: () => void }) {
  return (
    <div className="space-y-6">
      <Hero onBegin={onBegin} />
      <ArchitectureStrip />
      <CastSection />
      <BasesSection />
      <ScoringSection />
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
// In-game: big basis question (the "main screen toggle")
// ───────────────────────────────────────────────────────────────────

function BasisQuestion({
  pendingBasis,
  active,
  onSelect,
}: {
  pendingBasis: Basis | null;
  active: boolean;
  onSelect: (b: Basis) => void;
}) {
  return (
    <div className="space-y-4">
      <div className="text-center">
        <div className="text-[11px] uppercase tracking-[0.4em] text-terminal-dim mb-1">
          Choose your measurement basis
        </div>
        <div className="text-2xl md:text-3xl font-extrabold tracking-[0.15em] text-terminal-fg uppercase">
          How will you splice the qubit?
        </div>
        <div className="text-xs text-terminal-dim mt-1">
          Match Alice's basis to read her bit cleanly. Guess wrong and you
          collapse it.
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <BigBasisButton
          basis="Z"
          label="Rectilinear"
          symbol="|0⟩  |1⟩"
          shortcut="Z"
          tone="neon"
          disabled={!active}
          pending={pendingBasis === "Z"}
          onClick={() => onSelect("Z")}
        />
        <BigBasisButton
          basis="X"
          label="Diagonal"
          symbol="|+⟩  |−⟩"
          shortcut="X"
          tone="warn"
          disabled={!active}
          pending={pendingBasis === "X"}
          onClick={() => onSelect("X")}
        />
      </div>
    </div>
  );
}

function BigBasisButton({
  basis,
  label,
  symbol,
  shortcut,
  tone,
  onClick,
  disabled,
  pending,
}: {
  basis: Basis;
  label: string;
  symbol: string;
  shortcut: string;
  tone: "neon" | "warn";
  onClick: () => void;
  disabled: boolean;
  pending: boolean;
}) {
  const toneClass =
    tone === "neon"
      ? "border-terminal-neon/60 text-terminal-neon hover:shadow-neon hover:bg-terminal-neon/10"
      : "border-terminal-warn/60 text-terminal-warn hover:shadow-warn hover:bg-terminal-warn/10";
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`group relative rounded-md border-2 ${toneClass} bg-terminal-grid/40 p-6 md:p-8 text-left transition disabled:opacity-50 disabled:cursor-not-allowed`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.3em] text-terminal-dim">
            Eve basis
          </div>
          <div className="flex items-baseline gap-3 mt-1">
            <span className="text-6xl md:text-7xl font-black tracking-tight leading-none">
              [{basis}]
            </span>
            <span className="text-2xl md:text-3xl font-extrabold tracking-[0.15em] uppercase">
              {label}
            </span>
          </div>
          <div className="text-base md:text-lg text-terminal-fg/80 mt-3 tracking-widest">
            {symbol}
          </div>
        </div>
        <div className="flex flex-col items-end gap-3 shrink-0">
          <span className="kbd text-base px-3 py-1">{shortcut}</span>
          {pending ? (
            <Atom size={26} className="animate-spin" />
          ) : (
            <Target size={26} className="opacity-70 group-hover:opacity-100" />
          )}
        </div>
      </div>
    </button>
  );
}

// ───────────────────────────────────────────────────────────────────
// Debrief — manual advance with "Next Packet"
// ───────────────────────────────────────────────────────────────────

function DebriefPanel({
  round,
  onNext,
  onAbort,
}: {
  round: CompletedRound;
  onNext: () => void;
  onAbort: () => void;
}) {
  const matched = round.response.bob_result === round.setup.alice_bit;
  const collapsed = round.response.wavefunction_collapsed;

  const headline =
    round.outcome === "clean_steal"
      ? "Clean Steal"
      : round.outcome === "missed"
        ? "Window Missed"
        : "Detected";
  const headlineTone: Tone =
    round.outcome === "clean_steal"
      ? "ok"
      : round.outcome === "missed"
        ? "warn"
        : "alert";
  const glow: "ok" | "warn" | "alert" =
    round.outcome === "clean_steal"
      ? "ok"
      : round.outcome === "missed"
        ? "warn"
        : "alert";

  return (
    <Panel
      title="Round Debrief"
      icon={<Eye size={14} />}
      glow={glow}
      right={
        <button
          onClick={onAbort}
          className="text-[10px] uppercase tracking-[0.3em] text-terminal-dim hover:text-terminal-alert flex items-center gap-1 px-2 py-1 rounded border border-terminal-border hover:border-terminal-alert/60 transition"
          title="Stop (Esc)"
        >
          <Square size={12} /> Stop
        </button>
      }
    >
      <div className="space-y-5">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div
            className={`text-3xl md:text-4xl font-black tracking-[0.15em] uppercase ${TONE_TEXT[headlineTone]}`}
          >
            {headline}
          </div>
          <div className="text-xs text-terminal-dim">
            {round.outcome === "clean_steal" &&
              "Your basis matched Alice's. Deterministic read — channel undisturbed."}
            {round.outcome === "detected" &&
              "Wrong basis → wavefunction collapsed → Bob disagreed with Alice."}
            {round.outcome === "missed" &&
              "Coherence window expired before you measured. Streak reset."}
          </div>
        </div>

        <ChannelDiagram
          alice_basis={round.setup.alice_basis}
          alice_bit={round.setup.alice_bit}
          eve_basis={round.eve_basis}
          bob_basis={round.setup.bob_basis}
          showAliceState={true}
          showBobResult={true}
          bob_result={round.response.bob_result}
          collapsed={collapsed}
        />

        {round.outcome !== "missed" &&
          (collapsed ? (
            <div className="border border-terminal-alert/60 bg-terminal-alert/10 rounded p-3 text-xs leading-relaxed text-terminal-fg flex gap-3">
              <AlertTriangle
                size={18}
                className="text-terminal-alert shrink-0 mt-0.5"
              />
              <div>
                <div className="text-terminal-alert font-bold uppercase tracking-[0.25em] text-[11px] mb-1">
                  Observer Effect — Wavefunction Collapsed
                </div>
                <p>
                  You measured a qubit prepared in the{" "}
                  <span className="text-terminal-warn">
                    {basisLabel(round.setup.alice_basis)}
                  </span>{" "}
                  basis using the{" "}
                  <span className="text-terminal-warn">
                    {basisLabel(round.eve_basis)}
                  </span>{" "}
                  projector. The state was a uniform superposition in your
                  basis, so the outcome was a 50/50 draw and the qubit
                  re-prepared in your basis — irreversibly. {matched ? "Bob got lucky this time." : "Bob disagreed with Alice and the QBER ticked up."}
                </p>
              </div>
            </div>
          ) : (
            <div className="border border-terminal-ok/40 bg-terminal-ok/5 rounded p-3 text-xs leading-relaxed text-terminal-fg flex gap-3">
              <Atom size={18} className="text-terminal-ok shrink-0 mt-0.5" />
              <div>
                <div className="text-terminal-ok font-bold uppercase tracking-[0.25em] text-[11px] mb-1">
                  Aligned Basis — Deterministic Read
                </div>
                <p>
                  Your projector matched Alice's preparation basis, so the
                  measurement was deterministic and the wavefunction did not
                  collapse into a different basis. Eve's bit equals Alice's bit;
                  Bob's outcome is dictated by whether his basis matches yours.
                </p>
              </div>
            </div>
          ))}

        <div className="flex flex-wrap items-center justify-end gap-3 pt-2 border-t border-terminal-border">
          <button
            onClick={onAbort}
            className="px-4 py-2 rounded border border-terminal-border text-terminal-dim font-bold uppercase tracking-[0.3em] text-xs hover:text-terminal-alert hover:border-terminal-alert/60 transition flex items-center gap-2"
          >
            <Square size={12} /> Stop
          </button>
          <button
            onClick={onNext}
            className="px-5 py-3 rounded border border-terminal-neon/70 bg-terminal-neon/10 text-terminal-neon font-extrabold uppercase tracking-[0.35em] text-sm hover:bg-terminal-neon/20 hover:shadow-neon transition flex items-center gap-2"
            title="Begin next packet (Space)"
          >
            Next Packet <ChevronRight size={16} />
            <span className="kbd ml-2">Space</span>
          </button>
        </div>
      </div>
    </Panel>
  );
}

// ───────────────────────────────────────────────────────────────────
// App shell + state machine
// ───────────────────────────────────────────────────────────────────

export default function App() {
  const [status, setStatus] = useState<GameStatus>("idle");
  const [round, setRound] = useState<RoundSetup | null>(null);
  const [remainingMs, setRemainingMs] = useState(COHERENCE_MS);
  const [keysStolen, setKeysStolen] = useState(0);
  const [stealthStreak, setStealthStreak] = useState(0);
  const [alarm, setAlarm] = useState(0);
  const [lastRound, setLastRound] = useState<CompletedRound | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingBasis, setPendingBasis] = useState<Basis | null>(null);
  const [roundIndex, setRoundIndex] = useState(1);

  const roundStartRef = useRef<number>(0);
  const tickRef = useRef<number | null>(null);
  // Bumped on begin / abort so a late fetch can recognise it was orphaned.
  const opTokenRef = useRef<number>(0);

  const clearTick = useCallback(() => {
    if (tickRef.current !== null) {
      window.clearInterval(tickRef.current);
      tickRef.current = null;
    }
  }, []);

  // Round timer ───────────────────────────────────────────────
  useEffect(() => {
    if (status !== "armed") {
      clearTick();
      return;
    }
    roundStartRef.current = performance.now();
    setRemainingMs(COHERENCE_MS);
    tickRef.current = window.setInterval(() => {
      const elapsed = performance.now() - roundStartRef.current;
      const remaining = Math.max(0, COHERENCE_MS - elapsed);
      setRemainingMs(remaining);
      if (remaining <= 0) {
        clearTick();
        handleMissed();
      }
    }, TICK_MS);
    return clearTick;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, round]);

  // Cleanup on unmount.
  useEffect(() => {
    return () => clearTick();
  }, [clearTick]);

  // begin / advance / abort ───────────────────────────────────
  const startRound = useCallback(() => {
    setError(null);
    setRound(makeRound());
    setRoundIndex((n) => n + 1);
    setStatus("armed");
  }, []);

  const beginOperation = useCallback(() => {
    opTokenRef.current += 1;
    clearTick();
    setKeysStolen(0);
    setStealthStreak(0);
    setAlarm(0);
    setLastRound(null);
    setRoundIndex(1);
    setError(null);
    setPendingBasis(null);
    setRound(makeRound());
    setStatus("armed");
  }, [clearTick]);

  const abortOperation = useCallback(() => {
    opTokenRef.current += 1;
    clearTick();
    setPendingBasis(null);
    setError(null);
    setStatus("idle");
  }, [clearTick]);

  const handleMissed = useCallback(() => {
    if (!round) return;
    const now = performance.now();
    setStealthStreak(0);
    const synthetic: CompletedRound = {
      setup: round,
      eve_basis: "Z",
      response: {
        qasm_generated:
          "// COHERENCE WINDOW EXPIRED\n// no basis selected — qubit decohered before Eve could splice the line",
        eve_result: 0,
        bob_result: 0,
        wavefunction_collapsed: false,
      },
      outcome: "missed",
      startedAt: roundStartRef.current,
      resolvedAt: now,
    };
    setLastRound(synthetic);
    setStatus("debrief");
  }, [round]);

  // POST /api/intercept ───────────────────────────────────────
  const intercept = useCallback(
    async (eve_basis: Basis) => {
      if (!round || status !== "armed") return;
      const token = opTokenRef.current;
      clearTick();
      setStatus("resolving");
      setPendingBasis(eve_basis);
      setError(null);

      const payload: InterceptPayload = {
        alice_bit: round.alice_bit,
        alice_basis: round.alice_basis,
        eve_basis,
        bob_basis: round.bob_basis,
      };

      try {
        const res = await fetch(`${API_URL}/api/intercept`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (token !== opTokenRef.current) return;
        if (!res.ok) throw new Error(`Worker responded ${res.status}`);
        const data = (await res.json()) as InterceptResponse;
        if (token !== opTokenRef.current) return;

        const matched = data.bob_result === round.alice_bit;
        const outcome: CompletedRound["outcome"] = matched
          ? "clean_steal"
          : "detected";

        let nextAlarm = alarm;
        if (matched) {
          setKeysStolen((k) => k + 1);
          setStealthStreak((s) => s + 1);
        } else {
          nextAlarm = Math.min(100, alarm + ALARM_STEP);
          setAlarm(nextAlarm);
          setStealthStreak(0);
        }

        const completed: CompletedRound = {
          setup: round,
          eve_basis,
          response: data,
          outcome,
          startedAt: roundStartRef.current,
          resolvedAt: performance.now(),
        };
        setLastRound(completed);

        if (nextAlarm >= 100) {
          setStatus("compromised");
          return;
        }
        setStatus("debrief"); // wait for user to click "Next Packet"
      } catch (err) {
        if (token !== opTokenRef.current) return;
        const msg =
          err instanceof Error
            ? err.message
            : "Unknown error talking to quantum worker";
        setError(`Quantum worker unreachable: ${msg}`);
        setStatus("armed");
        const elapsed = performance.now() - roundStartRef.current;
        const remaining = Math.max(0, COHERENCE_MS - elapsed);
        if (remaining > 0) {
          tickRef.current = window.setInterval(() => {
            const e2 = performance.now() - roundStartRef.current;
            const r2 = Math.max(0, COHERENCE_MS - e2);
            setRemainingMs(r2);
            if (r2 <= 0) {
              clearTick();
              handleMissed();
            }
          }, TICK_MS);
        } else {
          handleMissed();
        }
      } finally {
        if (token === opTokenRef.current) setPendingBasis(null);
      }
    },
    [round, status, alarm, handleMissed, clearTick],
  );

  // Keyboard: Z, X, Space (begin / next), Esc (stop) ──────────
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const k = e.key;
      const lower = k.toLowerCase();
      if (k === "Escape") {
        if (status !== "idle") {
          e.preventDefault();
          abortOperation();
        }
        return;
      }
      if (k === " " || k === "Enter") {
        if (status === "idle" || status === "compromised") {
          e.preventDefault();
          beginOperation();
          return;
        }
        if (status === "debrief") {
          e.preventDefault();
          startRound();
          return;
        }
      }
      if (status === "armed") {
        if (lower === "z") void intercept("Z");
        if (lower === "x") void intercept("X");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [status, intercept, beginOperation, abortOperation, startRound]);

  // ──────────────────────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────────────────────
  const inGame =
    status === "armed" || status === "resolving" || status === "debrief";

  return (
    <div className="relative z-10 min-h-screen flex flex-col">
      <TopBar
        inGame={inGame || status === "compromised"}
        onAbort={abortOperation}
      />

      <main className="flex-1 max-w-7xl w-full mx-auto px-4 lg:px-8 py-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="flex flex-col gap-6 min-w-0">
          {status === "idle" && <WelcomeScreen onBegin={beginOperation} />}

          {status === "compromised" && lastRound && (
            <CompromisedPanel
              onRestart={beginOperation}
              onAbort={abortOperation}
              lastRound={lastRound}
            />
          )}

          {(status === "armed" || status === "resolving") && round && (
            <Panel
              title={`Live Channel · Round #${roundIndex}`}
              icon={<Crosshair size={14} />}
              glow={remainingMs < 1500 ? "alert" : "neon"}
              right={
                <button
                  onClick={abortOperation}
                  className="text-[10px] uppercase tracking-[0.3em] text-terminal-dim hover:text-terminal-alert flex items-center gap-1 px-2 py-1 rounded border border-terminal-border hover:border-terminal-alert/60 transition"
                  title="Stop (Esc)"
                >
                  <Square size={12} /> Stop
                </button>
              }
            >
              <div className="space-y-5">
                <CoherenceBar
                  remainingMs={remainingMs}
                  active={status === "armed"}
                />
                <ChannelDiagram
                  alice_basis={round.alice_basis}
                  alice_bit={round.alice_bit}
                  eve_basis={null}
                  bob_basis={round.bob_basis}
                  showAliceState={false}
                  showBobResult={false}
                />
                <BasisQuestion
                  pendingBasis={pendingBasis}
                  active={status === "armed"}
                  onSelect={(b) => void intercept(b)}
                />
                {error && (
                  <div className="border border-terminal-alert/60 bg-terminal-alert/10 rounded p-2 text-xs text-terminal-alert flex items-center gap-2">
                    <AlertTriangle size={14} /> {error}
                  </div>
                )}
              </div>
            </Panel>
          )}

          {status === "debrief" && lastRound && (
            <DebriefPanel
              round={lastRound}
              onNext={startRound}
              onAbort={abortOperation}
            />
          )}

          {status !== "idle" && (
            <QasmBlock qasm={lastRound?.response.qasm_generated ?? null} />
          )}
        </div>

        <aside className="flex flex-col gap-6">
          <Panel title="Operative HUD" icon={<Activity size={14} />}>
            <div className="grid grid-cols-2 gap-3">
              <Stat
                label="Keys Stolen"
                value={keysStolen}
                icon={<KeyRound size={18} />}
                tone="neon"
              />
              <Stat
                label="Stealth Streak"
                value={stealthStreak}
                icon={<Waves size={18} />}
                tone={stealthStreak >= 3 ? "ok" : "dim"}
              />
            </div>
            <div className="mt-3">
              <AlarmMeter value={alarm} />
            </div>
            {status !== "idle" && (
              <button
                onClick={abortOperation}
                className="mt-4 w-full py-2 rounded border border-terminal-alert/60 text-terminal-alert font-bold uppercase tracking-[0.35em] text-xs bg-terminal-alert/5 hover:bg-terminal-alert/10 hover:shadow-alert transition flex items-center justify-center gap-2"
                title="Stop / abort current operation (Esc)"
              >
                <Square size={14} /> Stop Operation
              </button>
            )}
          </Panel>

          <Panel title="Doctrine" icon={<Terminal size={14} />}>
            <ul className="text-xs text-terminal-fg/90 space-y-2 leading-relaxed">
              <li className="flex gap-2">
                <Binary size={14} className="text-terminal-neon shrink-0 mt-0.5" />
                <span>
                  Match Alice's basis → deterministic read, no collapse.
                </span>
              </li>
              <li className="flex gap-2">
                <AlertTriangle
                  size={14}
                  className="text-terminal-alert shrink-0 mt-0.5"
                />
                <span>
                  Wrong basis → 50/50 result, wavefunction collapses, Bob
                  disagrees with Alice ½ the time.
                </span>
              </li>
              <li className="flex gap-2">
                <Timer size={14} className="text-terminal-warn shrink-0 mt-0.5" />
                <span>
                  5.0s coherence window. Decoherence resets your streak.
                </span>
              </li>
            </ul>
          </Panel>

          <Panel title="Endpoint" icon={<RadioTower size={14} />}>
            <div className="text-[11px] uppercase tracking-[0.25em] text-terminal-dim mb-1">
              Quantum Worker
            </div>
            <div className="text-xs text-terminal-fg break-all">
              <span className="text-terminal-ok">POST</span> {API_URL}
              /api/intercept
            </div>
          </Panel>
        </aside>
      </main>

      <footer className="text-[10px] uppercase tracking-[0.3em] text-terminal-dim border-t border-terminal-border py-3 text-center">
        phantom-node :: classical/quantum split :: edge-deployed BB84 splice
      </footer>

      <style>{`
        .kbd {
          display: inline-block;
          padding: 0 0.5rem;
          border: 1px solid #2d2160;
          border-bottom-width: 2px;
          border-radius: 4px;
          color: #ff5cf2;
          background: #110a30;
          font-size: 0.7rem;
          letter-spacing: 0.1em;
          font-weight: 700;
        }
      `}</style>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
// Top bar + Compromised banner
// ───────────────────────────────────────────────────────────────────

function TopBar({
  inGame,
  onAbort,
}: {
  inGame: boolean;
  onAbort: () => void;
}) {
  return (
    <header className="border-b border-terminal-border bg-terminal-panel/70 backdrop-blur-sm">
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Atom size={22} className="text-terminal-neon animate-flicker" />
            <span className="absolute -inset-1 rounded-full animate-pulseRing" />
          </div>
          <div className="leading-tight">
            <div className="text-terminal-neon font-bold tracking-[0.3em] text-sm uppercase">
              Phantom Node
            </div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-terminal-dim">
              BB84 :: Quantum Cyber-Warfare
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="hidden md:flex items-center gap-4 text-[10px] uppercase tracking-[0.3em] text-terminal-dim">
            <span className="flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-terminal-ok animate-pulse" />
              edge link green
            </span>
            <span className="flex items-center gap-1.5">
              <Cpu size={12} className="text-terminal-neon" /> qpu sim · single
              qubit
            </span>
          </div>
          {inGame && (
            <button
              onClick={onAbort}
              className="text-[10px] uppercase tracking-[0.3em] text-terminal-alert flex items-center gap-1.5 px-3 py-1.5 rounded border border-terminal-alert/50 hover:bg-terminal-alert/10 hover:shadow-alert transition"
              title="Stop operation (Esc)"
            >
              <Square size={12} /> Stop Operation
            </button>
          )}
        </div>
      </div>
    </header>
  );
}

function CompromisedPanel({
  onRestart,
  onAbort,
  lastRound,
}: {
  onRestart: () => void;
  onAbort: () => void;
  lastRound: CompletedRound;
}) {
  return (
    <Panel
      title="Operation Compromised"
      icon={<Bomb size={14} />}
      glow="alert"
      right={
        <button
          onClick={onAbort}
          className="text-[10px] uppercase tracking-[0.3em] text-terminal-dim hover:text-terminal-alert flex items-center gap-1 px-2 py-1 rounded border border-terminal-border hover:border-terminal-alert/60 transition"
          title="Stop (Esc)"
        >
          <Square size={12} /> Stop
        </button>
      }
    >
      <div className="space-y-5">
        <div className="flex items-start gap-3">
          <Skull size={28} className="text-terminal-alert shrink-0" />
          <div>
            <div className="text-2xl md:text-3xl font-extrabold tracking-[0.18em] uppercase text-terminal-alert mb-1">
              Channel Burned
            </div>
            <p className="text-sm text-terminal-fg leading-relaxed">
              QBER hit 100%. Alice and Bob detected the splice and burned the
              channel. All keys you'd lifted are useless against a fresh
              one-time pad.
            </p>
          </div>
        </div>

        <ChannelDiagram
          alice_basis={lastRound.setup.alice_basis}
          alice_bit={lastRound.setup.alice_bit}
          eve_basis={lastRound.eve_basis}
          bob_basis={lastRound.setup.bob_basis}
          showAliceState
          showBobResult
          bob_result={lastRound.response.bob_result}
          collapsed={lastRound.response.wavefunction_collapsed}
        />

        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={onRestart}
            className="py-3 rounded border border-terminal-alert/60 text-terminal-alert font-bold uppercase tracking-[0.4em] text-sm bg-terminal-alert/5 hover:bg-terminal-alert/10 hover:shadow-alert transition"
          >
            <span className="inline-flex items-center gap-2">
              <Flame size={16} /> Restart
            </span>
          </button>
          <button
            onClick={onAbort}
            className="py-3 rounded border border-terminal-border text-terminal-dim font-bold uppercase tracking-[0.4em] text-sm hover:border-terminal-info/60 hover:text-terminal-info transition"
          >
            <span className="inline-flex items-center gap-2">
              <Power size={16} /> Back to Briefing
            </span>
          </button>
        </div>
      </div>
    </Panel>
  );
}
